//* Libraries imports
import { motion } from "framer-motion";
import { Card, Badge } from "react-bootstrap";

//* Icons imports
import {
  BsCalendarFill,
  BsCheckCircleFill,
  BsXCircleFill,
  BsClockFill,
  BsInfoCircleFill,
} from "react-icons/bs";
import Icon from "../../../../components/common/Icon";

//* Styles imports
import "./enrollment-header.scss";

//* Types
import { Enrollment } from "../../../../schemas/common/enrollment/enrollment";

type EnrollmentHeaderProps = {
  enrollment: Enrollment;
};

export function EnrollmentHeader({ enrollment }: EnrollmentHeaderProps) {
  const getBadgeVariant = (
    status: "deferida" | "indeferida" | "pendente" | null
  ) => {
    switch (status) {
      case "deferida":
        return "success";
      case "indeferida":
        return "danger";
      case "pendente":
        return "warning";
      default:
        return "secondary";
    }
  };

  const getStatusIcon = (
    status: "deferida" | "indeferida" | "pendente" | null
  ) => {
    switch (status) {
      case "deferida":
        return BsCheckCircleFill;
      case "indeferida":
        return BsXCircleFill;
      case "pendente":
        return BsClockFill;
      default:
        return BsClockFill;
    }
  };

  const getStatusText = (
    status: "deferida" | "indeferida" | "pendente" | null
  ) => {
    switch (status) {
      case "deferida":
        return "Deferida";
      case "indeferida":
        return "Indeferida";
      case "pendente":
        return "Pendente";
      default:
        return "Não Avaliada";
    }
  };

  const formatScheduleDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "full",
      timeStyle: "short",
    }).format(date);
  };

  const getCriteriaDescription = (type: string) => {
    switch (type) {
      case "sisu":
        return "Aprovados no SISU (Ensino Médio).";
      case "prouni_fies":
        return "Aprovados via PROUNI/FIES (Ensino Médio).";
      case "faculdade_privada_enem":
        return "Aprovados em vestibular privado ou acesso via nota do ENEM.";
      case "estagio_jovem_aprendiz":
        return "Aprovados em seleção para Estágio ou Jovem Aprendiz.";
      case "emprego_privado":
        return "Aprovados em seleção para emprego privado.";
      case "concurso":
        return "Aprovados em concurso público.";
      default:
        return "";
    }
  };

  return (
    <motion.div
      className="enrollment-header-wrapper mb-4"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="enrollment-header-card shadow-lg border-0">
        <Card.Body className="p-4">
          <div className="enrollment-header-grid">
            <motion.div
              className="status-highlight-section"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <div className="status-label text-muted mb-2">
                <small className="fw-semibold text-uppercase">
                  Status da Avaliação
                </small>
              </div>
              <Badge
                bg={getBadgeVariant(enrollment.statusAvaliacao)}
                className="status-badge px-4 py-3 d-inline-flex align-items-center gap-2"
              >
                <Icon
                  icon={getStatusIcon(enrollment.statusAvaliacao)}
                  size={20}
                  className="status-icon"
                />
                <span className="fs-5 fw-bold">
                  {getStatusText(enrollment.statusAvaliacao)}
                </span>
              </Badge>

              {enrollment.statusAvaliacao && enrollment.dataAvaliacao && (
                <div className="mt-3 text-muted small">
                  <div className="d-flex align-items-center gap-2">
                    <BsCalendarFill size={14} />
                    <span>
                      Avaliada em:{" "}
                      {new Date(enrollment.dataAvaliacao).toLocaleDateString(
                        "pt-BR"
                      )}
                    </span>
                  </div>
                  {enrollment.avaliador && (
                    <div className="mt-1">
                      Avaliador(a):{" "}
                      <span className="fw-semibold">
                        {enrollment.avaliador.name}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            <motion.div
              className="schedule-highlight-section"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              <div className="schedule-label text-muted mb-2">
                <small className="fw-semibold text-uppercase">
                  Data Agendada
                </small>
              </div>
              <div className="schedule-card p-3 rounded">
                <div className="d-flex align-items-center gap-3">
                  <div className="schedule-icon-wrapper">
                    <Icon
                      icon={BsCalendarFill}
                      size={32}
                      className="schedule-icon"
                    />
                  </div>
                  <div className="schedule-info">
                    <div className="schedule-date fs-4 fw-bold">
                      {formatScheduleDate(enrollment.dataAgendamento)}
                    </div>
                    <div className="schedule-subtitle text-muted small mt-1">
                      {enrollment.processo?.name || "Processo Seletivo"}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {enrollment.criterioSelecao && (
              <motion.div
                className="criteria-highlight-section"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                style={{ gridColumn: "1 / -1" }}
              >
                <div className="criteria-label text-muted mb-2">
                  <small className="fw-semibold text-uppercase">
                    Critério de Seleção
                  </small>
                </div>
                <div className="criteria-card p-3 rounded">
                  <div className="d-flex align-items-center gap-3">
                    <div className="criteria-icon-wrapper">
                      <Icon
                        icon={BsInfoCircleFill}
                        size={32}
                        className="criteria-icon"
                      />
                    </div>
                    <div className="criteria-info">
                      <div className="criteria-text fs-5 fw-bold">
                        {getCriteriaDescription(enrollment.criterioSelecao)}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {enrollment.observacoesAvaliacao && (
            <motion.div
              className="observations-section mt-4 pt-4 border-top"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.4 }}
            >
              <div className="observations-label text-muted mb-2">
                <small className="fw-semibold text-uppercase">
                  Observações da Avaliação
                </small>
              </div>
              <div className="observations-content p-3 bg-light rounded">
                <p className="mb-0">{enrollment.observacoesAvaliacao}</p>
              </div>
            </motion.div>
          )}
        </Card.Body>
      </Card>
    </motion.div>
  );
}
